import { useState } from 'react'
import { Button, Card, DatePicker, Form, Popover, Segmented, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { reportFilterFields, reportQuerySchema, type ReportDimension } from '@super-mega/server/contracts/analytics'
import { PAST_RANGE_PRESETS } from '@/const/range-presets.ts'
import { REPORT_LABELS } from './const/reports.ts'
import { ReportAppliedFilters } from './report-applied-filters.tsx'
import { ReportDimensionSelect } from './report-dimension-select.tsx'
import { reportSearchSchema, type ReportSearch } from './report-search.ts'
import type { ReportNavigate } from './report-table.tsx'
import { currentMonth } from './utils/format.ts'
import { reportScope } from './utils/report-query.ts'

type FilterValues = Partial<ReportSearch> & {
  dateMode?: 'month' | 'range'
  selectedMonth: Dayjs
  dateRange?: [Dayjs, Dayjs]
}

export function ReportFilters({
  search,
  dimension,
  navigate,
  refresh,
  loading
}: {
  search: ReportSearch
  dimension: ReportDimension
  navigate: ReportNavigate
  refresh: () => Promise<unknown>
  loading: boolean
}) {
  const [form] = Form.useForm<FilterValues>()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const mode = Form.useWatch('dateMode', form) ?? (search.dateFrom ? 'range' : 'month')
  const scope = reportScope(search, dimension)
  const dimensions = Object.keys(REPORT_LABELS) as ReportDimension[]
  const selectedCount = dimensions.filter((key) => search[reportFilterFields[key]]?.length).length
  const draftDimensions = Form.useWatch(
    (values: FilterValues) =>
      JSON.stringify(dimensions.map((key) => (values[reportFilterFields[key]] ?? []).toSorted((a, b) => a - b))),
    { form, preserve: true }
  )
  const appliedDimensions = JSON.stringify(
    dimensions.map((key) => (search[reportFilterFields[key]] ?? []).toSorted((a, b) => a - b))
  )
  const hasPendingFilters = !!draftDimensions && draftDimensions !== appliedDimensions
  async function submit(submittedValues: FilterValues) {
    // 弹层首次打开前字段尚未注册，读取完整 Store 保留深链和持久化条件。
    const values: FilterValues = { ...form.getFieldsValue(true), ...submittedValues }
    const filters = Object.fromEntries(
      dimensions.map((key) => [
        reportFilterFields[key],
        values[reportFilterFields[key]]?.length ? values[reportFilterFields[key]] : undefined
      ])
    )
    const next = {
      ...search,
      ...filters,
      current: 1,
      month: values.dateMode === 'month' ? values.selectedMonth.format('YYYY-MM') : undefined,
      dateFrom: values.dateMode === 'range' ? values.dateRange?.[0].format('YYYY-MM-DD') : undefined,
      dateTo: values.dateMode === 'range' ? values.dateRange?.[1].format('YYYY-MM-DD') : undefined,
      granularity: values.granularity,
      orderKind: values.orderKind
    }
    const parsed = reportQuerySchema.safeParse({ ...next, dimension })
    if (!parsed.success) {
      form.setFields([{ name: 'dateRange', errors: parsed.error.issues.map((issue) => issue.message) }])
      return
    }
    setFiltersOpen(false)
    if (JSON.stringify(reportScope(next, dimension)) === JSON.stringify(scope)) {
      await refresh()
    } else {
      await navigate({ search: next })
    }
  }
  return (
    <Card classNames={{ body: 'px-5 py-4 sm:px-6' }}>
      <Form<FilterValues>
        name={`report-${dimension}-filters`}
        form={form}
        layout="vertical"
        initialValues={{
          ...search,
          granularity: search.granularity ?? 'day',
          dateMode: search.dateFrom ? 'range' : 'month',
          selectedMonth: dayjs(`${search.month ?? currentMonth()}-01`),
          dateRange: search.dateFrom && search.dateTo ? [dayjs(search.dateFrom), dayjs(search.dateTo)] : undefined
        }}
        onFinish={(values) => void submit(values)}
      >
        <div className="grid grid-cols-1 items-end gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
          <Form.Item name="dateMode" label="统计期间" className="mb-0!">
            <Segmented
              block
              options={[
                { label: '按月份', value: 'month' },
                { label: '自定义区间', value: 'range' }
              ]}
            />
          </Form.Item>
          {mode === 'month' ? (
            <Form.Item
              name="selectedMonth"
              label="月份"
              rules={[{ required: true, message: '请选择月份' }]}
              className="mb-0!"
            >
              <DatePicker
                className="w-full"
                picker="month"
                allowClear={false}
                minDate={dayjs('2000-01-01')}
                maxDate={dayjs().endOf('month')}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="dateRange"
              label="日期范围"
              rules={[{ required: true, message: '请选择起止日期' }]}
              className="mb-0!"
            >
              <DatePicker.RangePicker
                className="w-full"
                minDate={dayjs('2000-01-01')}
                maxDate={dayjs()}
                presets={PAST_RANGE_PRESETS}
              />
            </Form.Item>
          )}
          <Form.Item name="orderKind" label="订单来源" className="mb-0!">
            <Select
              allowClear
              placeholder="全部来源"
              options={[
                { label: '租赁订单', value: 'rental' },
                { label: '服务订单', value: 'service' }
              ]}
            />
          </Form.Item>
          <Form.Item name="granularity" label="趋势粒度" className="mb-0!">
            <Select
              options={[
                { label: '按天', value: 'day' },
                { label: '按月', value: 'month' }
              ]}
            />
          </Form.Item>
        </div>
        <div className="border-border mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
          <Popover
            trigger="click"
            placement="bottom"
            arrow={false}
            align={{ offset: [0, 0] }}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            classNames={{ root: 'w-[40rem]! max-w-[calc(100vw_-_2rem)]!', container: 'p-4!' }}
            title={
              <div className="mb-3 flex items-center justify-between gap-3">
                <span>组合维度筛选</span>
                <Button
                  type="text"
                  size="small"
                  aria-label="关闭组合筛选"
                  icon={<X className="size-4" aria-hidden />}
                  onClick={() => setFiltersOpen(false)}
                />
              </div>
            }
            content={
              <div>
                <div className="max-h-[min(55vh,25rem)] overflow-y-auto">
                  <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                    {dimensions.map((key) => (
                      <Form.Item key={key} name={reportFilterFields[key]} label={REPORT_LABELS[key]} className="mb-0!">
                        <ReportDimensionSelect dimension={key} />
                      </Form.Item>
                    ))}
                  </div>
                  <p className="text-content-muted my-3 text-xs leading-relaxed">
                    同一维度多选取并集，不同维度取交集；点击查询后应用到整页数据。
                  </p>
                </div>
                <div className="border-border flex justify-between gap-2 border-t pt-3">
                  <Button
                    onClick={() => {
                      form.setFieldsValue(Object.fromEntries(dimensions.map((key) => [reportFilterFields[key], []])))
                    }}
                  >
                    清空维度
                  </Button>
                  <Button type="primary" loading={loading} onClick={() => form.submit()}>
                    查询数据
                  </Button>
                </div>
              </div>
            }
          >
            <Button icon={<SlidersHorizontal className="size-4" aria-hidden />} aria-expanded={filtersOpen}>
              组合筛选{selectedCount ? ` · ${selectedCount} 个维度` : ''}
              {hasPendingFilters ? ' · 待应用' : ''}
            </Button>
          </Popover>
          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto">
            <Button
              onClick={() => {
                form.resetFields()
                void navigate({ search: { ...reportSearchSchema.parse({}), month: currentMonth(), view: search.view } })
              }}
            >
              重置全部
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} icon={<Search className="size-4" aria-hidden />}>
              查询数据
            </Button>
          </div>
        </div>
        {hasPendingFilters && (
          <p className="text-content-muted mt-3 mb-0 text-xs">组合筛选有未应用的修改，点击“查询数据”后生效。</p>
        )}
        <ReportAppliedFilters search={search} navigate={navigate} />
      </Form>
    </Card>
  )
}
